import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatBytes, getUploadFileForSubmit, prepareUploadFile } from '../utils/uploadFiles';

const RULES = {
  submitter_name: { required: true, label: 'Your name is required.' },
  submitter_email: {
    validate: (v) => {
      if (!v) return null; // optional
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Please enter a valid email address.';
      return null;
    },
  },
  title: {
    required: true,
    label: 'Please describe what needs attention.',
    validate: (v) => {
      if (v && v.length < 5) return 'Please be a bit more descriptive (at least 5 characters).';
      return null;
    },
  },
  description: {
    required: true,
    label: 'A description is required.',
    validate: (v) => {
      if (v && v.length < 10) return 'Please add a bit more detail (at least 10 characters).';
      return null;
    },
  },
  location: { required: true, label: 'Please select a location.' },
};

function validateField(name, value) {
  const rule = RULES[name];
  if (!rule) return null;
  if (rule.required && (!value || !value.trim())) return rule.label;
  if (rule.validate) return rule.validate(value);
  return null;
}

function validateAll(form) {
  const errors = {};
  for (const name of Object.keys(RULES)) {
    const err = validateField(name, form[name]);
    if (err) errors[name] = err;
  }
  return errors;
}

export default function SubmitRequest() {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [form, setForm] = useState({
    submitter_name: '',
    submitter_email: '',
    submitter_phone: '',
    title: '',
    description: '',
    location: '',
    specific_location: '',
    priority: 'medium',
  });
  const [files, setFiles] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear error on change if field was touched
    if (touched[name]) {
      const err = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: err }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleFiles = async (e) => {
    const newFiles = Array.from(e.target.files);
    e.target.value = '';
    if (newFiles.length === 0) return;

    setProcessingFiles(true);
    try {
      const preparedFiles = await Promise.all(newFiles.map(prepareUploadFile));
      setFiles(prev => [...prev, ...preparedFiles]);
    } finally {
      setProcessingFiles(false);
    }
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const toggleUseOriginal = (id) => {
    setFiles(prev => prev.map(file =>
      file.id === id ? { ...file, useOriginal: !file.useOriginal } : file
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    // Validate all fields
    const errors = validateAll(form);
    setFieldErrors(errors);
    // Mark everything as touched
    const allTouched = {};
    Object.keys(RULES).forEach(k => { allTouched[k] = true; });
    setTouched(allTouched);

    if (Object.keys(errors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const el = document.getElementById(firstErrorField);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);

    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (val) data.append(key, val);
      });
      files.forEach(file => {
        data.append('files', getUploadFileForSubmit(file));
      });

      await api.createTicket(data);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setServerError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="page container">
        <div className="success-page">
          <div className="success-icon">&#10003;</div>
          <h2>Request Submitted</h2>
          <p className="success-message">
            Thank you. The Trustees have been notified and will review your request.
            You can check its progress on the board at any time.
          </p>
          <div className="success-actions">
            <button className="btn btn-primary" onClick={() => navigate('/board')}>
              View the Board
            </button>
            <button className="btn btn-secondary" onClick={() => { setSuccess(false); setForm({ submitter_name: '', submitter_email: '', submitter_phone: '', title: '', description: '', location: '', specific_location: '', priority: 'medium' }); setFiles([]); setFieldErrors({}); setTouched({}); setProcessingFiles(false); }}>
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  const err = (name) => touched[name] && fieldErrors[name];

  return (
    <div className="page container">
      <div className="form-page">
        <div className="form-page-header">
          <h1 className="page-title">Submit a Request</h1>
          <p>Let us know what needs attention. Your name and contact info are only visible to the Trustees.</p>
        </div>

        {serverError && <div className="alert alert-error">{serverError}</div>}

        <form className="form-card glass" onSubmit={handleSubmit} noValidate>
          <div className={`form-group ${err('submitter_name') ? 'form-group-error' : ''}`}>
            <label className="form-label" htmlFor="submitter_name">Your Name *</label>
            <input id="submitter_name" name="submitter_name" className="form-input" value={form.submitter_name} onChange={handleChange} onBlur={handleBlur} />
            {err('submitter_name') && <p className="form-error">{fieldErrors.submitter_name}</p>}
          </div>

          <div className="form-row">
            <div className={`form-group ${err('submitter_email') ? 'form-group-error' : ''}`}>
              <label className="form-label" htmlFor="submitter_email">Email</label>
              <input id="submitter_email" name="submitter_email" type="email" className="form-input" value={form.submitter_email} onChange={handleChange} onBlur={handleBlur} placeholder="Optional" />
              {err('submitter_email') && <p className="form-error">{fieldErrors.submitter_email}</p>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="submitter_phone">Phone</label>
              <input id="submitter_phone" name="submitter_phone" type="tel" className="form-input" value={form.submitter_phone} onChange={handleChange} placeholder="Optional" />
            </div>
          </div>

          <div className={`form-group ${err('location') ? 'form-group-error' : ''}`}>
            <label className="form-label" htmlFor="location">Location *</label>
            <select id="location" name="location" className="form-select" value={form.location} onChange={handleChange} onBlur={handleBlur}>
              <option value="">Select a location…</option>
              <option value="church">Church</option>
              <option value="pastors_house">Pastor's House</option>
            </select>
            {err('location') && <p className="form-error">{fieldErrors.location}</p>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="specific_location">Specific Location</label>
            <input id="specific_location" name="specific_location" className="form-input" value={form.specific_location} onChange={handleChange} placeholder="e.g. Kitchen sink, Back parking lot" />
          </div>

          <div className={`form-group ${err('title') ? 'form-group-error' : ''}`}>
            <label className="form-label" htmlFor="title">What needs attention? *</label>
            <input id="title" name="title" className="form-input" value={form.title} onChange={handleChange} onBlur={handleBlur} placeholder="Brief summary" />
            {err('title') && <p className="form-error">{fieldErrors.title}</p>}
          </div>

          <div className={`form-group ${err('description') ? 'form-group-error' : ''}`}>
            <label className="form-label" htmlFor="description">Description *</label>
            <textarea id="description" name="description" className="form-textarea" value={form.description} onChange={handleChange} onBlur={handleBlur} placeholder="Tell us more about the issue…" rows={4} />
            {err('description') && <p className="form-error">{fieldErrors.description}</p>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="priority">How urgent is this?</label>
            <select id="priority" name="priority" className="form-select" value={form.priority} onChange={handleChange}>
              <option value="low">Low — when you get to it</option>
              <option value="medium">Medium — should be addressed soon</option>
              <option value="high">High — needs prompt attention</option>
              <option value="urgent">Urgent — safety concern or major issue</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Photos or Documents</label>
            <div className="file-upload" onClick={() => fileInputRef.current?.click()}>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" onChange={handleFiles} />
              <div className="file-upload-icon">+</div>
              <p>{processingFiles ? 'Preparing photos…' : 'Tap to add photos or PDFs'}</p>
              <p className="file-upload-hint">Large photos are compressed before upload. PDFs are unchanged. Up to 50MB total.</p>
            </div>
            {files.length > 0 && (
              <div className="file-list">
                {files.map((file) => {
                  const activeFile = file.useOriginal || !file.compressedFile ? file.originalFile : file.compressedFile;
                  const wasCompressed = !!file.compressedFile && !file.useOriginal;
                  return (
                  <div key={file.id} className="file-chip">
                    <span className="file-chip-main">
                      <span className="file-chip-name">{activeFile.name}</span>
                      <span className="file-chip-size">
                        {wasCompressed
                          ? `Compressed from ${formatBytes(file.originalFile.size)} to ${formatBytes(activeFile.size)}`
                          : file.compressedFile && file.useOriginal
                          ? `Using original ${formatBytes(file.originalFile.size)} · compressed version is ${formatBytes(file.compressedFile.size)}`
                          : `${formatBytes(activeFile.size)}${file.compressionError ? ' · using original' : ''}`}
                      </span>
                    </span>
                    {file.compressedFile && (
                      <button type="button" className="file-chip-action" onClick={() => toggleUseOriginal(file.id)}>
                        {file.useOriginal ? 'Compress' : 'Original'}
                      </button>
                    )}
                    <button type="button" className="file-chip-remove" onClick={() => removeFile(file.id)} aria-label="Remove file">&times;</button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={submitting || processingFiles}>
            {submitting ? 'Submitting…' : processingFiles ? 'Preparing photos…' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
